import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Snackbar,
  Alert,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import SelectAllIcon from "@mui/icons-material/SelectAll";

import { SupervisorUid } from "../components/constants";
// ---- Utilities ----
const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// Simple color presets for role chips
const COLOR_PRESETS = [
  { label: "Teal", value: "#00897B" },
  { label: "Indigo", value: "#3949AB" },
  { label: "Amber", value: "#FF8F00" },
  { label: "Pink", value: "#D81B60" },
  { label: "Cyan", value: "#0097A7" },
  { label: "Grey", value: "#546E7A" },
];

// Default role model
const emptyRole = {
  id: null,
  name: "",
  description: "",
  maxDaysPerYear: 0,
  carryOverDays: 0,
  requiresApproval: true,
  color: COLOR_PRESETS[0].value,
  supervisorId: null, // legacy
  supervisorIds: [], // preferred (ordered by priority: top = highest)
  supervisorSettings: [], // [{empid, bypassPriority, priority}]
};

export default function LeaveRolesPage() {
  const [employees, setEmployees] = useState([]); // [{empid, code, name}]
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [roles, setRoles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
  const [form, setForm] = useState(emptyRole);
  const [formError, setFormError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const SAVED_API = useMemo(
    () => localStorage.getItem("saved_sett_api_url") || "http://localhost:5100",
    [],
  );
  const base = normalizeBaseUrl(SAVED_API);

  // Dialog state: selected employees (multi) & supervisors (multi, ordered)
  const [selectedEmployees, setSelectedEmployees] = useState([]); // [{empid, code, name}]
  const [selectedSupervisors, setSelectedSupervisors] = useState([]); // [{empid, code, name}] in priority order

  const [supervisorAfterDays, setSupervisorAfterDays] = useState({});
  const [supervisorSprHr, setSupervisorSprHr] = useState({}); // { [empid]: 0|1 }


  
  const supervisorUidSet = useMemo(                                   // add
    () => new Set(SupervisorUid.map((u) => String(u).trim())),        // add
    [],                                                               //  add
  );                                                                  //  add
  const assignableEmployees = useMemo(                                //  add
    () => employees.filter((e) => !supervisorUidSet.has(String(e.empid))), //  add
    [employees, supervisorUidSet],                                    //  add
  );                                                                  // add



  const empLabel = (e) => {
    if (!e) return "";
    return String(
      e.name ??
        e.fullName ??
        e.FullName ??
        e.fullname ??
        e.FULLNAME ??
        e.employeeName ??
        e.EmployeeName ??
        e.UserName ??
        e.username ??
        e.code ??
        e.empid ??
        "",
    ).trim();
  };

  const empCode = (e) =>
    String(e?.code ?? e?.EmpCode ?? e?.empcode ?? "").trim();

  const empSearchLabel = (e) => {
    if (!e) return "";
    const name = empLabel(e);
    const code = empCode(e);
    return code ? `${code} — ${name}` : name;
  };

  // فلترة مخصصة: يبحث بالكود والاسم
  const filterByNameOrCode = (options, state) => {
    const q = String(state.inputValue ?? "")
      .trim()
      .toLowerCase();
    if (!q) return options;

    return options.filter((o) => {
      const name = String(empLabel(o)).toLowerCase();
      const code = String(empCode(o)).toLowerCase();
      const id = String(o?.empid ?? o?.id ?? "").toLowerCase();
      return (
        name.includes(q) ||
        code.includes(q) ||
        id.includes(q) ||
        `${code} ${name}`.includes(q)
      );
    });
  };

  const firstOnly = (full = "") => full.split(" ").filter(Boolean)[0] || full;

  // robust byIds (casts)
  const byIds = (ids = []) =>
    ids
      .map((id) => employees.find((e) => Number(e.empid) === Number(id)))
      .filter(Boolean);

  // ✅ Convert any supervisor shape (id / object) to an "employee-like" object
  const toEmpFromAny = (x) => {
    if (!x) return null;

    // id directly
    if (typeof x === "number" || typeof x === "string") {
      const id = Number(String(x).trim());
      if (!id) return null;

      const found = employees.find((e) => Number(e.empid) === id);
      if (found) return found;

      // ✅ placeholder (so Autocomplete shows it even before employees are loaded)
      return { empid: id, id, code: String(id), name: String(id) };
    }

    // object with id/empid
    const id = Number(x.empid ?? x.EmpId ?? x.empId ?? x.id ?? x.ID ?? 0) || 0;
    if (!id) return null;

    const found = employees.find((e) => Number(e.empid) === id);
    if (found) return found;

    const directName = String(
      x.name ??
        x.fullName ??
        x.FullName ??
        x.fullname ??
        x.employeeName ??
        x.EmployeeName ??
        x.UserName ??
        x.username ??
        "",
    ).trim();

    const fn = String(
      x.FName ?? x.fname ?? x.FstName ?? x.firstName ?? "",
    ).trim();
    const mn = String(x.MName ?? x.mname ?? "").trim();
    const ln = String(
      x.LName ?? x.lname ?? x.Family ?? x.lastName ?? "",
    ).trim();
    const name = directName || [fn, mn, ln].filter(Boolean).join(" ").trim();

    const code = String(
      x.code ?? x.EmpCode ?? x.empcode ?? x.Code ?? id,
    ).trim();

    return { empid: id, id, code, name: name || String(id) };
  };

  const resolveAssignedEmployees = (row) => {
    if (Array.isArray(row.employees))
      return row.employees.map(toEmpFromAny).filter(Boolean);
    if (Array.isArray(row.assignedEmployees))
      return row.assignedEmployees.map(toEmpFromAny).filter(Boolean);
    if (Array.isArray(row.employeeIds)) return byIds(row.employeeIds);
    return [];
  };

  // ✅ UPDATED: supervisors are always resolved from employees (like employees)
  const resolveSupervisors = (row) => {
    let list = [];

    if (Array.isArray(row.supervisorIds) && row.supervisorIds.length) {
      list = row.supervisorIds.map(toEmpFromAny).filter(Boolean);
    } else if (Array.isArray(row.supervisors) && row.supervisors.length) {
      list = row.supervisors.map(toEmpFromAny).filter(Boolean);
    } else if (
      row.supervisor?.empid ||
      row.supervisor?.id ||
      row.supervisor?.name
    ) {
      const one = toEmpFromAny(row.supervisor);
      list = one ? [one] : [];
    } else if (row.supervisorEmpId != null) {
      const one = toEmpFromAny(row.supervisorEmpId);
      list = one ? [one] : [];
    } else if (row.supervisorId != null) {
      const one = toEmpFromAny(row.supervisorId);
      list = one ? [one] : [];
    } else if (typeof row.supervisor === "string" && row.supervisor.trim()) {
      list = [{ empid: 0, id: 0, code: "", name: row.supervisor.trim() }];
    }

    // Optional: sort by supervisorSettings.priority if present (1 = highest)
    const prMap = (
      Array.isArray(row.supervisorSettings) ? row.supervisorSettings : []
    ).reduce((acc, s) => {
      const k = Number(s?.empid);
      if (k) acc[k] = Number(s?.priority ?? 0) || 0;
      return acc;
    }, {});
    if (list.length && Object.keys(prMap).length) {
      list = list
        .slice()
        .sort((a, b) => (prMap[a.empid] || 0) - (prMap[b.empid] || 0));
    }

    return list;
  };

  // ---- Fetch roles ----
  const fetchRoles = async () => {
    if (!base) {
      setLoading(false);
      setSnack({
        open: true,
        message: "API URL not set in Settings.",
        severity: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${base}/api/leave-roles`);
      const list = Array.isArray(data) ? data : [];
      setRoles(list);
      setFiltered(list);
    } catch {
      setSnack({
        open: true,
        message: "Failed to load privileges.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Preload employees ----
  useEffect(() => {
    if (employees.length || !base) return;

    const load = async () => {
      try {
        setEmployeesLoading(true);

        // const res = await axios.get(`${base}/api/employees`, {
        //   params: { excludeUids: SupervisorUid.join(",") },
        // });
        const res = await axios.get(`${base}/api/employees`);   

        const payload = res?.data;

        const normalizeEmp = (x) => {
          const empid =
            Number(x.empid ?? x.EmpId ?? x.empId ?? x.id ?? x.ID ?? 0) || 0;

          const code = String(
            x.code ?? x.EmpCode ?? x.empcode ?? x.Code ?? empid ?? "",
          ).trim();

          const directName = String(
            x.name ??
              x.fullName ??
              x.FullName ??
              x.fullname ??
              x.employeeName ??
              x.EmployeeName ??
              x.UserName ??
              x.username ??
              "",
          ).trim();

          const fst = String(
            x.FstName ?? x.firstName ?? x.FirstName ?? "",
          ).trim();
          const fam = String(x.Family ?? x.lastName ?? x.LastName ?? "").trim();

          const name =
            directName || [fst, fam].filter(Boolean).join(" ").trim();

          return {
            ...x,
            id: empid,
            empid,
            code,
            name,
          };
        };

        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.rows)
              ? payload.rows
              : [];

        setEmployees(list.map(normalizeEmp).filter((e) => e.empid && e.name));
      } catch (e) {
        console.error(
          "employees load failed:",
          e?.response?.data || e?.message || e,
        );
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };

    load();
  }, [base, employees.length]);

  // ---- Search ----
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFiltered(roles);
    } else {
      setFiltered(
        roles.filter(
          (r) =>
            r.name?.toLowerCase().includes(q) ||
            r.description?.toLowerCase().includes(q),
        ),
      );
    }
  }, [roles, search]);

  // ---- Dialog helpers ----
  const openCreate = () => {
    setForm(emptyRole);
    setFormError("");
    setDialogMode("create");
    setDialogOpen(true);
    setSelectedEmployees([]);
    setSelectedSupervisors([]);
    setSupervisorAfterDays({});
    setSupervisorSprHr({});
  };

  const openEdit = (role) => {
    // employees selection
    const hydratedSelected =
      Array.isArray(role.employees) && role.employees.length
        ? role.employees.map(toEmpFromAny).filter(Boolean)
        : Array.isArray(role.employeeIds)
          ? role.employeeIds.map(toEmpFromAny).filter(Boolean)
          : [];

    // supervisors selection (prefer API objects first)
    let hydratedSupervisors =
      Array.isArray(role.supervisors) && role.supervisors.length
        ? role.supervisors.map(toEmpFromAny).filter(Boolean)
        : Array.isArray(role.supervisorIds) && role.supervisorIds.length
          ? role.supervisorIds.map(toEmpFromAny).filter(Boolean)
          : role.supervisor
            ? [toEmpFromAny(role.supervisor)].filter(Boolean)
            : role.supervisorId != null
              ? [toEmpFromAny(role.supervisorId)].filter(Boolean)
              : [];

    // sort by priority if provided (1 = highest)
    const prMap = (
      Array.isArray(role.supervisorSettings) ? role.supervisorSettings : []
    ).reduce((acc, s) => {
      const k = Number(s?.empid);
      if (k) acc[k] = Number(s?.priority ?? 0) || 0;
      return acc;
    }, {});
    if (hydratedSupervisors.length && Object.keys(prMap).length) {
      hydratedSupervisors = hydratedSupervisors
        .slice()
        .sort((a, b) => (prMap[a.empid] || 0) - (prMap[b.empid] || 0));
    }

    // ✅ spr_hr seed from supervisorSettings (by empid)
    const priorSprHr = Array.isArray(role.supervisorSettings)
      ? role.supervisorSettings.reduce((acc, s) => {
          const k = Number(s?.empid);
          if (!k) return acc;
          const v = s?.spr_hr ?? s?.sprHr ?? s?.sprHR ?? 0;
          acc[k] = Number(v) === 1 ? 1 : 0;
          return acc;
        }, {})
      : {};

    const seededSprHr = {};
    hydratedSupervisors.forEach((s) => {
      seededSprHr[s.empid] = priorSprHr[s.empid] ?? 0;
    });

    setSupervisorSprHr(seededSprHr);
    // bypass seed
    const priorBypass = Array.isArray(role.supervisorSettings)
      ? role.supervisorSettings.reduce((acc, s) => {
          const k = Number(s?.empid);
          if (k) acc[k] = !!s.bypassPriority;
          return acc;
        }, {})
      : {};

    // ✅ afterDays seed
    const priorAfterDays = Array.isArray(role.supervisorSettings)
      ? role.supervisorSettings.reduce((acc, s) => {
          const k = Number(s?.empid);
          if (!k) return acc;
          const v = s?.afterDays ?? s?.after_days ?? s?.bypassAfterDays ?? 0;
          acc[k] = Number(v) || 0;
          return acc;
        }, {})
      : {};

    const seededAfterDays = {};
    hydratedSupervisors.forEach((s) => {
      seededAfterDays[s.empid] = priorAfterDays[s.empid] ?? 0;
    });

    setSelectedEmployees(hydratedSelected);
    setSelectedSupervisors(hydratedSupervisors);
    setSupervisorAfterDays(seededAfterDays);

    const seededBypass = {};

    hydratedSupervisors.forEach((s) => {
      seededBypass[s.empid] = priorBypass[s.empid] ?? false;
      seededAfterDays[s.empid] = priorAfterDays[s.empid] ?? 0; // ✅
    });

    setSelectedEmployees(hydratedSelected);
    setSelectedSupervisors(hydratedSupervisors);
    setSupervisorAfterDays(seededAfterDays); // ✅

    setForm({
      ...emptyRole,
      ...role,
      supervisorId: role.supervisorId ?? hydratedSupervisors[0]?.empid ?? null,
      supervisorIds: Array.isArray(role.supervisorIds)
        ? role.supervisorIds
        : hydratedSupervisors.map((s) => s.empid),
      supervisorSettings: Array.isArray(role.supervisorSettings)
        ? role.supervisorSettings
        : [],
    });

    setFormError("");
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(emptyRole);
    setFormError("");
    setSelectedEmployees([]);
    setSelectedSupervisors([]);
    setSupervisorSprHr({});

    setSupervisorAfterDays({}); // ✅
  };
  const validate = () => {
    if (!form.name.trim()) return "Role name is required.";
    return "";
  };

  // ---- Reorder helpers for supervisors ----
  const swap = (arr, i, j) => {
    if (i === j || i < 0 || j < 0 || i >= arr.length || j >= arr.length)
      return arr;
    const copy = arr.slice();
    const t = copy[i];
    copy[i] = copy[j];
    copy[j] = t;
    return copy;
  };

  const moveSupUp = (index) => {
    setSelectedSupervisors((prev) => swap(prev, index, index - 1));
  };

  const moveSupDown = (index) => {
    setSelectedSupervisors((prev) => swap(prev, index, index + 1));
  };

  // sync the 3 tables privelige , user,supervisor, to remote 
  const syncPrivileges = async () => {
    try {
      await axios.get(`${base}/api/privilege/sync`, { withCredentials: true });
    } catch {
      setSnack({
        open: true,
        message: "Saved locally, but sync to cloud failed.",
        severity: "warning",
      });
    }
  };  

  // ---- Create / Update ----
  const saveRole = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    if (!base) {
      setSnack({
        open: true,
        message: "API URL not set in Settings.",
        severity: "error",
      });
      return;
    }

    // ORDER MATTERS: top of selectedSupervisors is highest priority
    const supervisorIds = selectedSupervisors.map((e) => e.empid);
    const payload = {
      ...form,
      employeeIds: selectedEmployees.map((e) => e.empid),
      supervisorIds,
      supervisorId: supervisorIds.length ? supervisorIds[0] : null, // legacy field
      supervisorSettings: selectedSupervisors.map((s, idx) => ({
        empid: s.empid,
        afterDays: Number(supervisorAfterDays[s.empid]) || 0, // ✅ send to API
        priority: idx + 1, // 1 = highest
      })),
    };
    
    setFormError("");
    setSaving(true);
    try {
      if (dialogMode === "create") {
        const tempId = `temp-${Date.now()}`;
        const optimistic = {
          ...form,
          id: tempId,
          employeeIds: payload.employeeIds,
          supervisorIds: payload.supervisorIds,
          supervisorId: payload.supervisorId,
          supervisorSettings: selectedSupervisors.map((s, idx) => ({
            empid: s.empid,

            afterDays: Number(supervisorAfterDays[s.empid]) || 0, // ✅ NEW
            priority: idx + 1,
          })),
        };
        setRoles((prev) => [optimistic, ...prev]);

        let data;
        try {
          ({ data } = await axios.post(`${base}/api/leave-roles`, payload));
        } catch (e) {
          setRoles((prev) => prev.filter((r) => r.id !== tempId));
          throw e;
        }
        setRoles((prev) => prev.map((r) => (r.id === tempId ? data : r)));

        setSnack({
          open: true,
          message: "Privilege created.",
          severity: "success",
        });
      } else {
        const previous = roles.find((r) => r.id === form.id);
        const optimistic = {
          ...form,
          employeeIds: payload.employeeIds,
          supervisorIds: payload.supervisorIds,
          supervisorId: payload.supervisorId,
          supervisorSettings: selectedSupervisors.map((s, idx) => ({
            empid: s.empid,

            afterDays: Number(supervisorAfterDays[s.empid]) || 0, // ✅ NEW
            priority: idx + 1,
          })),
        };
        setRoles((prev) =>
          prev.map((r) => (r.id === form.id ? optimistic : r)),
        );

        try {
          await axios.put(`${base}/api/leave-roles/${form.id}`, payload);
          setSnack({
            open: true,
            message: "Privilege updated.",
            severity: "success",
          });
        } catch (e) {
          setRoles((prev) =>
            prev.map((r) => (r.id === previous.id ? previous : r)),
          );
          throw e;
        }
      }
      await fetchRoles();

      await syncPrivileges();

      closeDialog();
        } catch (e) {
      const conflicts = e.response?.data?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length) {
        const nameOf = (id) =>
          empLabel(employees.find((x) => Number(x.empid) === Number(id))) || String(id);
        const text = conflicts.map((c) => `${nameOf(c.empId)} (${c.prvName})`).join(", ");
        setFormError(`Already in another privilege: ${text}. Remove them from that privilege first.`);
      } else {
        setSnack({
          open: true,
          message: "Save failed. Please try again.",
          severity: "error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----
  const askDelete = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!base || !deletingId) {
      setConfirmOpen(false);
      return;
    }
    const backup = roles;
    setRoles((prev) => prev.filter((r) => r.id !== deletingId));
    try {
      await axios.delete(`${base}/api/leave-roles/${deletingId}`);
      setSnack({
        open: true,
        message: "Privilege deleted.",
        severity: "success",
      });
      await syncPrivileges();
    } catch {
      setRoles(backup);
      setSnack({ open: true, message: "Delete failed.", severity: "error" });
    } finally {
      setDeletingId(null);
      setConfirmOpen(false);
    }
  };

  // ---- Columns for DataGrid ----
  const columns = [
    {
      field: "name",
      headerName: "Privilege",
      headerAlign: "center",
      flex: 1.1,
      minWidth: 180,
      renderCell: (params) => (
        <Chip
          label={params.row.name}
          sx={{
            bgcolor: `${params.row.color || "#00897B"}20`,
            color: params.row.color || "#00897B",
            fontWeight: 700,
            borderRadius: 1.5,
          }}
        />
      ),
    },
    {
      field: "assignedEmployees",
      headerName: "Assigned employees",
      headerAlign: "center",
      flex: 1.3,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const list = resolveAssignedEmployees(params.row);
        if (!list.length)
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );

        const names = list.map((e) => e?.name).filter(Boolean);
        const first = firstOnly(names[0]);

        return (
          <Tooltip
            title={
              <React.Fragment>
                {names.map((n, i) => (
                  <div key={i}>{n}</div>
                ))}
              </React.Fragment>
            }
            arrow
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={first} size="small" />
              {names.length > 1 && (
                <Chip
                  label={`+${names.length - 1} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: "supervisor",
      headerName: "Supervisor(s)",
      headerAlign: "center",
      flex: 1.1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        // try resolved first
        const sups = resolveSupervisors(params.row);

        // fallback directly from API row if resolver fails
        const raw = Array.isArray(params.row?.supervisors)
          ? params.row.supervisors
          : [];
        const list = sups.length ? sups : raw;

        // ✅ use empLabel (same logic as employees) instead of s.name, checks all of 8 different field names until it finds one that isn't empty
        const names = list.map(empLabel).filter(Boolean);

        if (!names.length) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }

        const first = firstOnly(names[0]);

        return (
          <Tooltip
            title={
              <React.Fragment>
                {names.map((n, i) => (
                  <div key={i}>{n}</div>
                ))}
              </React.Fragment>
            }
            arrow
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={first} size="small" />
              {names.length > 1 && (
                <Chip
                  label={`+${names.length - 1} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",   // ← built-in prop, no CSS fighting needed
      align: "center", 
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => askDelete(params.row.id)}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const NoRows = () => (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#004d40" }}
      >
        No privileges
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Create your first privilege to get started.
      </Typography>
      <Button
        onClick={openCreate}
        variant="contained"
        startIcon={<AddRoundedIcon />}
        sx={{ borderRadius: 2 }}
      >
        Add privilege
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
        display: "flex",
        justifyContent: "center",
        background: `radial-gradient(1200px 600px at -10% -20%, rgba(0,121,107,0.18), transparent 60%),
                     radial-gradient(900px 500px at 120% 120%, rgba(0,151,167,0.16), transparent 60%),
                     linear-gradient(135deg, #e0f2f1 0%, #ffffff 60%)`,
        "&:before": {
          content: "''",
          position: "absolute",
          inset: 0,
          background: `url('/bg-texture.svg') center/cover no-repeat`,
          opacity: 0.06,
          pointerEvents: "none",
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: 1100,
          maxWidth: "98vw",
          borderRadius: 4,
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          bgcolor: "rgba(255,255,255,0.96)",
          boxShadow:
            "0 10px 30px rgba(0,0,0,.08), 0 20px 60px rgba(0,121,107,.15)",
          border: "1px solid",
          borderColor: "#b2dfdb",
        }}
      >
        {loading && (
          <LinearProgress
            color="success"
            sx={{ borderRadius: "4px 4px 0 0" }}
          />
        )}

        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "start", sm: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: "#00838f",
                  width: 56,
                  height: 56,
                  boxShadow: "0 8px 24px rgba(0,131,143,.30)",
                }}
              >
                <NotificationsActiveRoundedIcon fontSize="medium" />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#004d40" }}
                >
                  Privileges
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Define privilege scopes and approval rules.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search privileges…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              <Button
                variant="outlined"
                onClick={fetchRoles}
                startIcon={<InfoOutlinedIcon />}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={openCreate}
                startIcon={<AddRoundedIcon />}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#004d40",
                  ":hover": { bgcolor: "#003d33" },
                  boxShadow: "0 10px 24px rgba(0,77,64,.25)",
                }}
              >
                New privilege
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {/* Info row */}
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <ShieldOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
            <Typography variant="caption" color="text.secondary">
              Privileges determine permissions and whether manager approval is
              required.
            </Typography>
          </Stack>

          {/* DataGrid Table */}
          <Box sx={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={filtered}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              columns={columns}
              slots={{ noRowsOverlay: NoRows }}
              components={{ NoRowsOverlay: NoRows }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "rgba(0,150,136,0.06)",
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  width: "100%",
                  textAlign: "center",
                },
                "& .MuiDataGrid-row:hover": {
                  background: "rgba(0,150,136,0.04)",
                },
                borderColor: "#b2dfdb",
                borderRadius: 2,
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            bgcolor: "#ffffff",
            border: "1px solid",
            borderColor: "rgba(0,0,0,0.08)",
            boxShadow: "0 28px 80px rgba(0,0,0,.28)",
          },
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            height: 6,
            bgcolor: "linear-gradient(90deg, #00897B, #0097A7)",
            background:
              "linear-gradient(90deg, rgba(0,137,123,1) 0%, rgba(0,151,167,1) 100%)",
          }}
        />

        {/* Error */}
        {formError && (
          <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: 2 }}>
            {formError}
          </Alert>
        )}

        <DialogTitle sx={{ pb: 1.25, pt: 2.25 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                sx={{
                  bgcolor: "rgba(0,131,143,0.10)",
                  color: "#006064",
                  width: 48,
                  height: 48,
                  border: "1px solid rgba(0,131,143,0.25)",
                }}
              >
                <NotificationsActiveRoundedIcon />
              </Avatar>

              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 900, color: "#0f172a" }}
                >
                  {dialogMode === "create"
                    ? "Create privilege"
                    : "Edit privilege"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure assigned employees and supervisor escalation rules.
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={dialogMode === "create" ? "New" : "Editing"}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                bgcolor:
                  dialogMode === "create"
                    ? "rgba(0,137,123,0.10)"
                    : "rgba(0,151,167,0.10)",
                color: dialogMode === "create" ? "#00695C" : "#006064",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 2.5 }}>
          <Stack spacing={2.25}>
            {/* Section: Basics */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                bgcolor: "rgba(2,132,199,0.02)",
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                      Basics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Name and primary privilege settings.
                    </Typography>
                  </Stack>

                  <TextField
                    label="Privilege name"
                    required
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2.25 },
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Section: Assigned Employees */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                bgcolor: "#ffffff",
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                      Assigned employees
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select all employees who will use this privilege.
                    </Typography>
                  </Stack>

                  <Autocomplete
                    multiple
                    options={assignableEmployees}
                    loading={employeesLoading}
                    value={selectedEmployees}
                    onChange={(_, newVal) => setSelectedEmployees(newVal)}
                    getOptionLabel={empSearchLabel}
                    filterOptions={filterByNameOrCode}
                    isOptionEqualToValue={(opt, val) =>
                      Number(opt.empid) === Number(val.empid)
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.empid}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={empCode(option) || option.empid}
                            sx={{ borderRadius: 2 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {empLabel(option)}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employees"
                        placeholder="Type name or code"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <Tooltip title="Select all employees">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setSelectedEmployees(assignableEmployees)
                                  }
                                  sx={{
                                    mr: 0.5,
                                    bgcolor: "rgba(0,150,136,0.06)",
                                    "&:hover": {
                                      bgcolor: "rgba(0,150,136,0.12)",
                                    },
                                  }}
                                >
                                  <SelectAllIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2.25 },
                        }}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Section: Supervisors */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                bgcolor: "#ffffff",
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                      Supervisors & escalation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add supervisors, set priority, and define escalation delay
                      (days).
                    </Typography>
                  </Stack>

                  <Autocomplete
                    multiple
                    options={employees}
                    loading={employeesLoading}
                    value={selectedSupervisors}
                    onChange={(_, newVal) => {
                      setSelectedSupervisors(newVal);

                      setSupervisorAfterDays((prev) => {
                        const next = {};
                        newVal.forEach(
                          (s) => (next[s.empid] = Number(prev[s.empid]) || 0),
                        );
                        return next;
                      });

                      setSupervisorSprHr((prev) => {
                        const next = {};
                        newVal.forEach(
                          (s) => (next[s.empid] = prev[s.empid] ?? 0),
                        );
                        return next;
                      });
                    }}
                    getOptionLabel={empSearchLabel}
                    filterOptions={filterByNameOrCode}
                    isOptionEqualToValue={(opt, val) =>
                      Number(opt.empid) === Number(val.empid)
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.empid}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={empCode(option) || option.empid}
                            sx={{ borderRadius: 2 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {empLabel(option)}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supervisors"
                        placeholder="Type name or code"
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2.25 },
                        }}
                      />
                    )}
                  />

                  {/* Stylish supervisor list */}
                  {selectedSupervisors.length > 0 ? (
                    <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                      {selectedSupervisors.map((sup, idx) => {
                        const isHr =
                          Number(supervisorSprHr?.[sup.empid] ?? 0) === 1;

                        return (
                          <Card
                            key={sup.empid}
                            elevation={0}
                            sx={{
                              borderRadius: 2.75,
                              border: "1px solid",
                              borderColor: isHr
                                ? "rgba(239,68,68,0.28)"
                                : "#e0f2f1",
                              bgcolor: isHr
                                ? "rgba(239,68,68,0.08)"
                                : "#ffffff",
                              px: 1.75,
                              py: 1.35,
                              transition: "0.2s",
                              "&:hover": {
                                boxShadow: isHr
                                  ? "0 6px 18px rgba(239,68,68,.12)"
                                  : "0 6px 18px rgba(0,150,136,.12)",
                                borderColor: isHr
                                  ? "rgba(239,68,68,0.42)"
                                  : "#80cbc4",
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              {/* Left */}
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Avatar
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: isHr
                                      ? "rgba(239,68,68,0.16)"
                                      : "#00897B",
                                    color: isHr
                                      ? "rgba(185,28,28,1)"
                                      : "#ffffff",
                                    fontSize: 14,
                                    fontWeight: 900,
                                    border: "1px solid",
                                    borderColor: isHr
                                      ? "rgba(239,68,68,0.25)"
                                      : "rgba(0,0,0,0.06)",
                                  }}
                                >
                                  {idx + 1}
                                </Avatar>

                                <Stack spacing={0.15}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          color: "#0f172a",
                                        }}
                                      >
                                        {empLabel(sup)}
                                      </Typography>

                                      {Number(
                                        supervisorSprHr?.[sup.empid] ?? 0,
                                      ) === 1 && (
                                        <Chip
                                          label="HR"
                                          size="small"
                                          sx={{
                                            height: 22,
                                            fontSize: 11,
                                            fontWeight: 900,
                                            borderRadius: 2,
                                            bgcolor: "#ffebee",
                                            color: "#b71c1c",
                                            border: "1px solid #ef9a9a",
                                          }}
                                        />
                                      )}
                                    </Stack>

                                    {isHr && (
                                      <Chip
                                        size="small"
                                        label="HR"
                                        sx={{
                                          height: 22,
                                          borderRadius: 2,
                                          fontWeight: 900,
                                          bgcolor: "rgba(239,68,68,0.12)",
                                          color: "rgba(185,28,28,1)",
                                          border:
                                            "1px solid rgba(239,68,68,0.25)",
                                        }}
                                      />
                                    )}
                                  </Stack>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Employee #{sup.code}
                                  </Typography>
                                </Stack>
                              </Stack>

                              {/* Right */}
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Escalation delay"
                                  value={Number(
                                    supervisorAfterDays?.[sup.empid] ?? 0,
                                  )}
                                  onChange={(e) => {
                                    const n = Math.max(
                                      0,
                                      Number(e.target.value) || 0,
                                    );
                                    setSupervisorAfterDays((prev) => ({
                                      ...prev,
                                      [sup.empid]: n,
                                    }));
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        days
                                      </InputAdornment>
                                    ),
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{
                                    width: 170,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2.25,
                                    },
                                  }}
                                />

                                <Divider
                                  orientation="vertical"
                                  flexItem
                                  sx={{ mx: 0.5 }}
                                />

                                <Stack spacing={0.35}>
                                  <Tooltip title="Move up" arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => moveSupUp(idx)}
                                        disabled={idx === 0}
                                        sx={{
                                          bgcolor: "rgba(0,150,136,0.06)",
                                          "&:hover": {
                                            bgcolor: "rgba(0,150,136,0.12)",
                                          },
                                        }}
                                      >
                                        <ArrowUpwardRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title="Move down" arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => moveSupDown(idx)}
                                        disabled={
                                          idx === selectedSupervisors.length - 1
                                        }
                                        sx={{
                                          bgcolor: "rgba(0,150,136,0.06)",
                                          "&:hover": {
                                            bgcolor: "rgba(0,150,136,0.12)",
                                          },
                                        }}
                                      >
                                        <ArrowDownwardRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: "1px dashed rgba(0,0,0,0.18)",
                        bgcolor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No supervisors selected. Add at least one supervisor to
                        enable approvals.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.25,
            bgcolor: "rgba(0,0,0,0.02)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={closeDialog}
            variant="text"
            color="inherit"
            sx={{ fontWeight: 800 }}
          >
            Cancel
          </Button>

          {/* Sync remote / local privelige, user, supervisor !   */}

          <Stack direction="row" spacing={1}>
            <LoadingButton
              loading={saving}
              onClick={saveRole}
              variant="contained"
              sx={{
                borderRadius: 2.25,
                px: 2.25,
                bgcolor: "#0f766e",
                ":hover": { bgcolor: "#0b5f59" },
                boxShadow: "0 10px 24px rgba(15,118,110,.24)",
                fontWeight: 900,
              }}
            >
              {dialogMode === "create" ? "Create privilege" : "Save changes"}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete privilege?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={false}
            onClick={confirmDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
